import { clearStoredSessionId, getStoredSessionId } from "./auth.ts";

export interface CurrentUser {
  id: string;
  email: string;
}

export interface ApiApplication {
  id: string;
  name: string;
  clientId: string;
  clientSecretPrefix: string;
  redirectUris: string[];
  createdAt: number;
  issuerUrl: string;
}

export interface ApiAppUser {
  id: string;
  email: string;
  accessCodePrefix: string;
  createdAt: number;
}

export class UnauthenticatedError extends Error {
  constructor() {
    super("unauthenticated");
  }
}

export class ApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

interface ApiFetchOptions {
  method?: string;
  body?: unknown;
}

async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const sessionId = getStoredSessionId();
  if (!sessionId) {
    throw new UnauthenticatedError();
  }

  const headers = new Headers();
  headers.set("Authorization", `Bearer ${sessionId}`);

  let body: BodyInit | undefined;
  if (options.body !== undefined && options.body !== null) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(options.body);
  }

  const response = await fetch(path, { method: options.method, headers, body });

  if (response.status === 401) {
    clearStoredSessionId();
    throw new UnauthenticatedError();
  }
  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}) as { error?: string });
    const message = (errorBody as { error?: string }).error ?? response.statusText;
    throw new ApiError(response.status, message);
  }
  return (await response.json()) as T;
}

const appPath = (appId: string) =>
  `/api/applications/${encodeURIComponent(appId)}`;

export const api = {
  getCurrentUser: () => apiFetch<CurrentUser>("/api/me"),

  logout: () => apiFetch<{ ok: true }>("/api/logout", { method: "POST" }),

  listApplications: () =>
    apiFetch<{ applications: ApiApplication[] }>("/api/applications"),

  createApplication: (name: string) =>
    apiFetch<{ application: ApiApplication; clientSecret: string }>(
      "/api/applications",
      { method: "POST", body: { name } },
    ),

  getApplication: (appId: string) =>
    apiFetch<{ application: ApiApplication }>(appPath(appId)),

  renameApplication: (appId: string, name: string) =>
    apiFetch<{ application: ApiApplication }>(appPath(appId), {
      method: "PATCH",
      body: { name },
    }),

  deleteApplication: (appId: string) =>
    apiFetch<{ ok: true }>(appPath(appId), { method: "DELETE" }),

  rotateClientSecret: (appId: string) =>
    apiFetch<{ application: ApiApplication; clientSecret: string }>(
      `${appPath(appId)}/rotate-secret`,
      { method: "POST" },
    ),

  updateRedirectUris: (appId: string, redirectUris: string[]) =>
    apiFetch<{ application: ApiApplication }>(`${appPath(appId)}/redirect-uris`, {
      method: "PUT",
      body: { redirectUris },
    }),

  listAppUsers: (appId: string) =>
    apiFetch<{ users: ApiAppUser[] }>(`${appPath(appId)}/users`),

  createAppUser: (appId: string, email: string, accessCode: string) =>
    apiFetch<{ user: ApiAppUser }>(`${appPath(appId)}/users`, {
      method: "POST",
      body: { email, accessCode },
    }),

  deleteAppUser: (appId: string, appUserId: string) =>
    apiFetch<{ ok: true }>(
      `${appPath(appId)}/users/${encodeURIComponent(appUserId)}`,
      { method: "DELETE" },
    ),
};
